// ═══════════════════════════════════════════════════════════════════════════
// 🎤 VOICE CHAT NEW - Versione ULTRA Debug
// ═══════════════════════════════════════════════════════════════════════════
// ✅ Selettore microfono (evita BlackHole!)
// ✅ Monitor livello audio in tempo reale
// ✅ Switch ElevenLabs / OpenAI / Browser TTS
// ✅ Test connettività endpoint
// ✅ Pulsanti rapidi per test veloci
// ✅ Test speaker
// ✅ Export log per debug
// ✅ Confronto voci
// ✅ Cronologia sessione
// ✅ Statistiche utilizzo
// ✅ QR code per telefono
// Creato: 28 Novembre 2025
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useRef, useEffect, useCallback } from 'react';

const API_BASE_URL = 'https://api.semanticai.it';
const API_KEY = 'DonatApp2025!';
const APP_URL = 'https://donatapp.vercel.app/voice-new';

const SERVIZI_TTS = [
  { id: 'elevenlabs', nome: 'ElevenLabs', icona: '🎭' },
  { id: 'openai', nome: 'OpenAI', icona: '🤖' },
  { id: 'browser', nome: 'Browser', icona: '🌐' },
];

const VOCI_ELEVENLABS = [
  { id: 'bella', nome: 'Caterina', colore: '#10b981' },
  { id: 'dottore', nome: 'Dottore', colore: '#3b82f6' },
];

const VOCI_OPENAI = [
  { id: 'nova', nome: 'Nova', colore: '#8b5cf6' },
  { id: 'alloy', nome: 'Alloy', colore: '#6366f1' },
  { id: 'echo', nome: 'Echo', colore: '#0ea5e9' },
  { id: 'shimmer', nome: 'Shimmer', colore: '#ec4899' },
];

const FRASI_RAPIDE = [
  { emoji: '⏰', testo: 'Che ore sono?' },
  { emoji: '📅', testo: 'Che giorno è oggi?' },
  { emoji: '👋', testo: 'Ciao, come stai?' },
  { emoji: '🍝', testo: 'Ho fame' },
  { emoji: '💊', testo: 'Devo prendere le medicine?' },
  { emoji: '👨‍👩‍👧', testo: 'Dov\'è la mia famiglia?' },
  { emoji: '🏠', testo: 'Dove sono?' },
  { emoji: '😢', testo: 'Mi sento triste' },
];

const VoiceChatNew = () => {
  // Stati
  const [stato, setStato] = useState('idle');
  const [messaggioUtente, setMessaggioUtente] = useState('');
  const [rispostaAI, setRispostaAI] = useState('');
  const [errore, setErrore] = useState('');
  const [log, setLog] = useState([]);
  
  // Impostazioni
  const [servizioTTS, setServizioTTS] = useState('elevenlabs');
  const [voceElevenLabs, setVoceElevenLabs] = useState('bella');
  const [voceOpenAI, setVoceOpenAI] = useState('nova');
  const [modalita, setModalita] = useState('voce');
  const [testoInput, setTestoInput] = useState('');
  
  // Microfono
  const [microfoni, setMicrofoni] = useState([]);
  const [microfonoSelezionato, setMicrofonoSelezionato] = useState('');
  const [livelloAudio, setLivelloAudio] = useState(0);
  const [monitorandoAudio, setMonitorandoAudio] = useState(false);
  
  // Connettività
  const [connettivita, setConnettivita] = useState({ transcribe: null, chat: null, ttsElevenlabs: null, ttsOpenai: null });
  const [testInCorso, setTestInCorso] = useState(false);
  
  // Tempi e stats
  const [tempi, setTempi] = useState({ trascrizione: null, chat: null, tts: null, totale: null });
  const [stats, setStats] = useState({ conv: 0, err: 0, avgMs: 0, times: [] });
  
  // UI
  const [cronologia, setCronologia] = useState([]);
  const [mostraCronologia, setMostraCronologia] = useState(false);
  const [mostraQR, setMostraQR] = useState(false);
  const [confrontoVoci, setConfrontoVoci] = useState(false);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const audioContextRef = useRef(null);

  // Log
  const addLog = useCallback((msg, tipo = 'info') => {
    const ts = new Date().toLocaleTimeString('it-IT');
    setLog(p => [...p.slice(-40), { ts, msg, tipo }]);
  }, []);

  // Carica microfoni
  const loadMics = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = devices.filter(d => d.kind === 'audioinput');
      setMicrofoni(mics);
      
      const realMic = mics.find(m => 
        !m.label.toLowerCase().includes('blackhole') &&
        !m.label.toLowerCase().includes('virtual')
      );
      if (realMic) {
        setMicrofonoSelezionato(realMic.deviceId);
        addLog(`🎤 ${realMic.label}`, 'success');
      } else if (mics.length) {
        setMicrofonoSelezionato(mics[0].deviceId);
      }
      addLog(`📱 ${mics.length} microfoni`, 'info');
    } catch (e) {
      addLog(`❌ Mic: ${e.message}`, 'error');
    }
  };

  // Monitor audio
  const toggleMonitor = async () => {
    if (monitorandoAudio) {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (audioContextRef.current) audioContextRef.current.close();
      setMonitorandoAudio(false);
      setLivelloAudio(0);
      addLog('🎚️ Monitor stop', 'info');
      return;
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: microfonoSelezionato ? { deviceId: { exact: microfonoSelezionato } } : true
      });
      streamRef.current = stream;
      
      const ctx = new AudioContext();
      audioContextRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      analyserRef.current = analyser;
      
      const data = new Uint8Array(analyser.frequencyBinCount);
      const update = () => {
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b) / data.length;
        setLivelloAudio(Math.min(100, avg * 1.5));
        animationRef.current = requestAnimationFrame(update);
      };
      update();
      setMonitorandoAudio(true);
      addLog('🎚️ Monitor on', 'success');
    } catch (e) {
      addLog(`❌ ${e.message}`, 'error');
    }
  };

  // Test speaker
  const testSpeaker = () => {
    addLog('🔊 Test speaker...', 'info');
    const u = new SpeechSynthesisUtterance('Test audio, uno due tre');
    u.lang = 'it-IT';
    u.onend = () => addLog('✅ Speaker OK', 'success');
    speechSynthesis.speak(u);
  };

  // Test connettività
  const testConn = async () => {
    setTestInCorso(true);
    addLog('🔍 Test...', 'info');
    const r = { transcribe: null, chat: null, ttsElevenlabs: null, ttsOpenai: null };
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/voice/transcribe`, { method: 'POST', headers: { 'x-api-key': API_KEY } });
      r.transcribe = res.status === 400 || res.ok;
    } catch { r.transcribe = false; }
    addLog(`Whisper: ${r.transcribe ? '✅' : '❌'}`, r.transcribe ? 'success' : 'error');
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/semantic/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({ userInput: 'test', userId: 1 })
      });
      r.chat = res.ok;
    } catch { r.chat = false; }
    addLog(`Chat: ${r.chat ? '✅' : '❌'}`, r.chat ? 'success' : 'error');
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/voice/tts-multivoce`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({ text: 'Test', forcedVoice: 'bella' })
      });
      const d = await res.json();
      r.ttsElevenlabs = d.success === true;
    } catch { r.ttsElevenlabs = false; }
    addLog(`11Labs: ${r.ttsElevenlabs ? '✅' : '❌'}`, r.ttsElevenlabs ? 'success' : 'error');
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/voice/tts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({ text: 'Test', voice: 'nova' })
      });
      r.ttsOpenai = res.ok;
    } catch { r.ttsOpenai = false; }
    addLog(`OpenAI: ${r.ttsOpenai ? '✅' : '❌'}`, r.ttsOpenai ? 'success' : 'error');
    
    setConnettivita(r);
    setTestInCorso(false);
  };

  // Registrazione
  const startRec = async () => {
    if (monitorandoAudio) toggleMonitor();
    setErrore(''); setMessaggioUtente(''); setRispostaAI('');
    setTempi({ trascrizione: null, chat: null, tts: null, totale: null });
    
    try {
      const mic = microfoni.find(m => m.deviceId === microfonoSelezionato);
      addLog(`🎤 ${mic?.label || 'default'}`, 'info');
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: microfonoSelezionato ? { deviceId: { exact: microfonoSelezionato }, echoCancellation: true } : { echoCancellation: true }
      });
      streamRef.current = stream;
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        addLog(`📦 ${(blob.size/1024).toFixed(1)}KB`, 'info');
        await processAudio(blob);
      };
      
      mediaRecorderRef.current.start();
      setStato('recording');
      addLog('🎙️ PARLA!', 'success');
    } catch (e) {
      addLog(`❌ ${e.message}`, 'error');
      setErrore(e.message);
      setStato('idle');
    }
  };

  const stopRec = () => {
    if (mediaRecorderRef.current && stato === 'recording') {
      mediaRecorderRef.current.stop();
      streamRef.current?.getTracks().forEach(t => t.stop());
    }
  };

  // Process audio
  const processAudio = async (blob) => {
    setStato('thinking');
    const t0 = Date.now();
    
    try {
      addLog('📝 STT...', 'info');
      const t1 = Date.now();
      const fd = new FormData();
      fd.append('audio', blob, 'rec.webm');
      
      const res = await fetch(`${API_BASE_URL}/api/voice/transcribe`, {
        method: 'POST', headers: { 'x-api-key': API_KEY }, body: fd
      });
      if (!res.ok) throw new Error('STT fail');
      
      const d = await res.json();
      const txt = d.text;
      setTempi(p => ({ ...p, trascrizione: Date.now() - t1 }));
      addLog(`✅ "${txt}"`, 'success');
      setMessaggioUtente(txt);
      
      if (!txt?.trim()) throw new Error('Vuoto');
      await genResponse(txt, t0);
    } catch (e) {
      addLog(`❌ ${e.message}`, 'error');
      setErrore(e.message);
      setStato('idle');
      setStats(s => ({ ...s, err: s.err + 1 }));
    }
  };

  // Process text
  const processTxt = async (txt = testoInput) => {
    if (!txt?.trim()) return;
    setErrore(''); setMessaggioUtente(txt); setRispostaAI('');
    setTempi({ trascrizione: null, chat: null, tts: null, totale: null });
    await genResponse(txt, Date.now());
  };

  // Generate response + audio
  const genResponse = async (txt, t0) => {
    setStato('thinking');
    
    try {
      addLog('🤖 AI...', 'info');
      const t2 = Date.now();
      
      const res = await fetch(`${API_BASE_URL}/api/semantic/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({ userInput: txt, userId: 1 })
      });
      if (!res.ok) throw new Error('Chat fail');
      
      const d = await res.json();
      const resp = d.response || 'Ciao!';
      setTempi(p => ({ ...p, chat: Date.now() - t2 }));
      addLog(`✅ AI ok`, 'success');
      setRispostaAI(resp);
      
      setStato('speaking');
      const t3 = Date.now();
      let ok = false;
      
      if (servizioTTS === 'elevenlabs') ok = await try11Labs(resp);
      else if (servizioTTS === 'openai') ok = await tryOpenAI(resp);
      
      if (!ok && servizioTTS !== 'browser') addLog('⚠️ Fallback', 'warning');
      if (!ok || servizioTTS === 'browser') await speakWithBrowser(resp);
      
      const tot = Date.now() - t0;
      setTempi(p => ({ ...p, tts: Date.now() - t3, totale: tot }));
      
      setStats(s => {
        const times = [...s.times.slice(-19), tot];
        return { conv: s.conv + 1, err: s.err, avgMs: Math.round(times.reduce((a,b)=>a+b,0)/times.length), times };
      });
      
      setCronologia(c => [...c.slice(-9), { u: txt, ai: resp, ms: tot, svc: servizioTTS, ts: new Date().toLocaleTimeString('it-IT') }]);
    } catch (e) {
      addLog(`❌ ${e.message}`, 'error');
      setErrore(e.message);
      setStato('idle');
      setStats(s => ({ ...s, err: s.err + 1 }));
    }
  };

  // TTS
  const try11Labs = async (txt) => {
    try {
      addLog(`🎭 ${voceElevenLabs}...`, 'info');
      const res = await fetch(`${API_BASE_URL}/api/voice/tts-multivoce`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({ text: txt, forcedVoice: voceElevenLabs })
      });
      const d = await res.json();
      if (d.success && d.audio) {
        addLog('✅ 11Labs', 'success');
        await playAudio(`data:audio/mpeg;base64,${d.audio}`);
        return true;
      }
      return false;
    } catch { return false; }
  };

  const tryOpenAI = async (txt) => {
    try {
      addLog(`🤖 ${voceOpenAI}...`, 'info');
      const res = await fetch(`${API_BASE_URL}/api/voice/tts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({ text: txt, voice: voceOpenAI })
      });
      if (!res.ok) return false;
      const d = await res.json();
      if (d.audio) {
        addLog('✅ OpenAI', 'success');
        await playAudio(`data:audio/mpeg;base64,${d.audio}`);
        return true;
      }
      return false;
    } catch { return false; }
  };

  const speakWithBrowser = (txt) => new Promise(resolve => {
    const u = new SpeechSynthesisUtterance(txt);
    u.lang = 'it-IT'; u.rate = 0.9;
    u.onend = () => { addLog('✅ Browser', 'success'); setStato('idle'); resolve(); };
    u.onerror = () => { setStato('idle'); resolve(); };
    speechSynthesis.speak(u);
  });

  const playAudio = (src) => new Promise(resolve => {
    const a = new Audio(src);
    a.onended = () => { setStato('idle'); resolve(); };
    a.onerror = () => { setStato('idle'); resolve(); };
    a.play().catch(() => { setStato('idle'); resolve(); });
  });

  // Export log
  const exportLog = () => {
    navigator.clipboard.writeText(log.map(l => `[${l.ts}] ${l.msg}`).join('\n'));
    addLog('📋 Copiato!', 'success');
  };

  // Click
  const onClick = () => stato === 'idle' ? startRec() : stato === 'recording' ? stopRec() : null;

  // Init
  useEffect(() => {
    loadMics();
    testConn();
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  // Render helpers
  const voci = servizioTTS === 'elevenlabs' ? VOCI_ELEVENLABS : servizioTTS === 'openai' ? VOCI_OPENAI : [];
  const voce = servizioTTS === 'elevenlabs' ? voceElevenLabs : voceOpenAI;
  const setVoce = servizioTTS === 'elevenlabs' ? setVoceElevenLabs : setVoceOpenAI;

  return (
    <div style={S.container}>
      {/* Header */}
      <div style={S.header}>
        <h1 style={S.title}>🎤 Voice Test</h1>
        <div style={S.hBtns}>
          <button onClick={() => setMostraQR(!mostraQR)} style={S.iBtn}>📱</button>
          <button onClick={() => setMostraCronologia(!mostraCronologia)} style={S.iBtn}>📜</button>
        </div>
      </div>

      {/* QR */}
      {mostraQR && (
        <div style={S.card}>
          <img src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(APP_URL)}`} alt="QR" style={{display:'block',margin:'0 auto',borderRadius:8}} />
          <div style={{fontSize:9,textAlign:'center',marginTop:6,color:'#666'}}>{APP_URL}</div>
        </div>
      )}

      {/* Stats */}
      <div style={S.stats}>💬{stats.conv} ❌{stats.err} ⏱️{stats.avgMs||'-'}ms</div>

      {/* Connettività */}
      <div style={S.card}>
        <div style={S.ch}>📡 <span><button onClick={testSpeaker} style={S.sm}>🔊</button><button onClick={testConn} disabled={testInCorso} style={S.sm}>{testInCorso?'⏳':'🔄'}</button></span></div>
        <div style={S.g4}>
          <B n="STT" ok={connettivita.transcribe}/><B n="AI" ok={connettivita.chat}/><B n="11L" ok={connettivita.ttsElevenlabs}/><B n="OAI" ok={connettivita.ttsOpenai}/>
        </div>
      </div>

      {/* Microfono */}
      <div style={S.card}>
        <div style={S.ch}>🎤 <button onClick={toggleMonitor} style={{...S.sm,background:monitorandoAudio?'#ef4444':'#10b981'}}>{monitorandoAudio?'⏹️':'▶️'}</button></div>
        <select value={microfonoSelezionato} onChange={e=>setMicrofonoSelezionato(e.target.value)} style={S.sel}>
          {microfoni.map(m=><option key={m.deviceId} value={m.deviceId}>{m.label||m.deviceId.slice(0,8)}</option>)}
        </select>
        <div style={S.lvl}><div style={{...S.lvlF,width:`${livelloAudio}%`}}/></div>
      </div>

      {/* Modalità */}
      <div style={S.card}>
        <div style={S.row}>
          <button onClick={()=>setModalita('voce')} style={{...S.tog,...(modalita==='voce'?S.togOn:{})}}>🎤</button>
          <button onClick={()=>setModalita('testo')} style={{...S.tog,...(modalita==='testo'?S.togOn:{})}}>⌨️</button>
        </div>
      </div>

      {/* TTS */}
      <div style={S.card}>
        <div style={S.row}>
          {SERVIZI_TTS.map(s=>(
            <button key={s.id} onClick={()=>setServizioTTS(s.id)} style={{...S.tts,...(servizioTTS===s.id?S.ttsOn:{})}}>{s.icona}</button>
          ))}
        </div>
      </div>

      {/* Voci */}
      {servizioTTS!=='browser' && voci.length>0 && (
        <div style={S.card}>
          <div style={S.row}>
            {voci.map(v=>(
              <button key={v.id} onClick={()=>setVoce(v.id)} style={{...S.vBtn,background:voce===v.id?v.colore:'#e5e7eb',color:voce===v.id?'#fff':'#333'}}>{v.nome}</button>
            ))}
          </div>
        </div>
      )}

      {/* Quick */}
      <div style={S.card}>
        <div style={S.qGrid}>
          {FRASI_RAPIDE.map((f,i)=>(
            <button key={i} onClick={()=>processTxt(f.testo)} style={S.qBtn} disabled={stato!=='idle'}>{f.emoji}</button>
          ))}
        </div>
      </div>

      {/* Input */}
      {modalita==='testo' ? (
        <div style={S.card}>
          <textarea value={testoInput} onChange={e=>setTestoInput(e.target.value)} placeholder="Scrivi..." style={S.ta} rows={2}/>
          <button onClick={()=>processTxt()} disabled={stato!=='idle'} style={S.send}>📤</button>
        </div>
      ) : (
        <div style={S.center}>
          <button onClick={onClick} disabled={stato==='thinking'||stato==='speaking'}
            style={{...S.main,background:stato==='recording'?'#ef4444':stato==='thinking'?'#f59e0b':stato==='speaking'?'#10b981':'#3b82f6'}}>
            {stato==='idle'&&'🎤 Parla'}{stato==='recording'&&'🛑 Stop'}{stato==='thinking'&&'🤔'}{stato==='speaking'&&'🔊'}
          </button>
        </div>
      )}

      {/* Tempi */}
      {tempi.totale && (
        <div style={S.card}>
          <div style={S.g4}>
            {tempi.trascrizione&&<T n="STT" ms={tempi.trascrizione}/>}<T n="AI" ms={tempi.chat}/><T n="TTS" ms={tempi.tts}/><T n="TOT" ms={tempi.totale} b/>
          </div>
        </div>
      )}

      {/* Messages */}
      {messaggioUtente && <div style={S.uMsg}>👤 {messaggioUtente}</div>}
      {rispostaAI && <div style={S.aMsg}>🤖 {rispostaAI}</div>}
      {errore && <div style={S.eMsg}>❌ {errore}</div>}

      {/* Cronologia */}
      {mostraCronologia && cronologia.length>0 && (
        <div style={S.card}>
          <div style={{maxHeight:120,overflowY:'auto'}}>
            {cronologia.slice().reverse().map((c,i)=>(
              <div key={i} style={{padding:6,borderBottom:'1px solid #eee',fontSize:10}}>
                <div style={{color:'#999'}}>{c.ts} • {c.svc} • {c.ms}ms</div>
                <div style={{color:'#1e40af'}}>👤 {c.u}</div>
                <div style={{color:'#166534'}}>🤖 {c.ai.slice(0,50)}...</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Log */}
      <div style={S.log}>
        <div style={S.lh}>📋 <span><button onClick={exportLog} style={S.lBtn}>📋</button><button onClick={()=>setLog([])} style={S.lBtn}>✕</button></span></div>
        <div style={S.lc}>
          {log.slice(-15).map((l,i)=>(
            <div key={i} style={{padding:'2px 0',color:l.tipo==='error'?'#f87171':l.tipo==='success'?'#4ade80':'#9ca3af',fontSize:9,fontFamily:'monospace'}}>
              [{l.ts}] {l.msg}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Helpers
const B = ({n,ok}) => <div style={{padding:4,borderRadius:4,fontSize:9,textAlign:'center',background:ok===null?'#f3f4f6':ok?'#dcfce7':'#fee2e2',color:ok===null?'#666':ok?'#166534':'#991b1b'}}>{ok===null?'⏳':ok?'✅':'❌'}{n}</div>;
const T = ({n,ms,b}) => <div style={{background:'#f3f4f6',padding:4,borderRadius:4,fontSize:9,display:'flex',justifyContent:'space-between',fontWeight:b?700:400}}><span>{n}</span><span style={{color:ms>3000?'#ef4444':ms>1500?'#f59e0b':'#10b981'}}>{ms}</span></div>;

// Styles
const S = {
  container:{maxWidth:500,margin:'0 auto',padding:10,fontFamily:'system-ui',background:'#f3f4f6',minHeight:'100vh'},
  header:{textAlign:'center',padding:12,background:'#fff',borderRadius:10,marginBottom:8,position:'relative'},
  title:{fontSize:18,fontWeight:700,margin:0},
  hBtns:{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',display:'flex',gap:4},
  iBtn:{width:28,height:28,borderRadius:6,border:'none',background:'#f3f4f6',cursor:'pointer'},
  stats:{textAlign:'center',fontSize:11,color:'#666',padding:'4px 0'},
  card:{background:'#fff',borderRadius:8,padding:10,marginBottom:6},
  ch:{fontSize:11,fontWeight:600,marginBottom:6,display:'flex',justifyContent:'space-between',alignItems:'center'},
  g4:{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:4},
  row:{display:'flex',gap:6},
  sm:{padding:'3px 6px',background:'#374151',color:'#fff',border:'none',borderRadius:4,fontSize:9,cursor:'pointer',marginLeft:4},
  sel:{width:'100%',padding:6,borderRadius:6,border:'1px solid #ddd',fontSize:11,marginBottom:4},
  lvl:{height:6,background:'#e5e7eb',borderRadius:3,overflow:'hidden'},
  lvlF:{height:'100%',background:'linear-gradient(90deg,#10b981,#f59e0b,#ef4444)',transition:'width 0.1s'},
  tog:{flex:1,padding:8,borderRadius:6,border:'none',fontSize:14,cursor:'pointer',background:'#e5e7eb'},
  togOn:{background:'#3b82f6',color:'#fff'},
  tts:{flex:1,padding:8,borderRadius:6,border:'2px solid #e5e7eb',background:'#fff',cursor:'pointer',fontSize:16},
  ttsOn:{borderColor:'#10b981',background:'#ecfdf5'},
  vBtn:{padding:'6px 10px',borderRadius:6,border:'none',cursor:'pointer',fontSize:11,fontWeight:500},
  qGrid:{display:'grid',gridTemplateColumns:'repeat(8,1fr)',gap:4},
  qBtn:{padding:6,borderRadius:4,border:'1px solid #e5e7eb',background:'#fff',cursor:'pointer',fontSize:14},
  ta:{width:'100%',padding:8,borderRadius:6,border:'1px solid #ddd',fontSize:13,resize:'none',boxSizing:'border-box',marginBottom:6},
  send:{width:'100%',padding:8,background:'#3b82f6',color:'#fff',border:'none',borderRadius:6,fontSize:14,cursor:'pointer'},
  center:{display:'flex',justifyContent:'center',padding:'10px 0'},
  main:{padding:'14px 28px',fontSize:14,fontWeight:600,color:'#fff',border:'none',borderRadius:12,boxShadow:'0 4px 12px rgba(0,0,0,0.2)',cursor:'pointer'},
  uMsg:{background:'#dbeafe',padding:8,borderRadius:6,marginBottom:4,color:'#1e40af',fontSize:12},
  aMsg:{background:'#dcfce7',padding:8,borderRadius:6,marginBottom:4,color:'#166534',fontSize:12},
  eMsg:{background:'#fee2e2',padding:8,borderRadius:6,marginBottom:4,color:'#991b1b',fontSize:12},
  log:{background:'#1f2937',borderRadius:8,padding:8,marginTop:6},
  lh:{color:'#9ca3af',fontSize:9,marginBottom:4,display:'flex',justifyContent:'space-between'},
  lc:{maxHeight:100,overflowY:'auto'},
  lBtn:{background:'none',border:'none',color:'#9ca3af',cursor:'pointer',fontSize:9},
};

export default VoiceChatNew;
